// usePermissions.tsx
import { useSelector } from "react-redux";
import { ApplicationState } from "store";
import { SubscriptionPlan, SUBSCRIPTION_DATA } from "common/data/subscription";

export interface Permission {
	model: string;
	parent_model?: string;
	name: string;
	value?: string;
	type?: string;
}

interface Role {
	title: string;
	permissions: Permission[];
}

interface UserProfile {
	role?: Role;
	subscription?: SubscriptionPlan;
}

/**
 * usePermissions hook returns a checkPermission function.
 */
export function usePermissions() {
	const user: UserProfile =
		useSelector((state: ApplicationState) => state.auth.userProfile) || {};

	const permissions: Permission[] = user.role?.permissions || [];

	// Use the subscription from the user profile; if absent, default to a minimal config.
	const userSubscription: SubscriptionPlan =
		user.subscription && user.subscription.modules
			? user.subscription
			: { ...SUBSCRIPTION_DATA, ...user.subscription };

	/**
	 * Granular subscription check with default reason.
	 */
	const isFeatureAllowed = (
		module: string,
		feature: string,
		action: string,
		parentModule?: string
	): { allowed: boolean; locked: boolean; reason: string } => {
		if (!userSubscription.modules) {
			return {
				allowed: true,
				locked: true,
				reason: "Please upgrade your subscription to access this feature."
			};
		}
		const norm = (s?: string) => s?.toLowerCase();
		let moduleConfig = userSubscription.modules?.[norm(module) ?? ""];

		if (!moduleConfig && parentModule) {
			moduleConfig = userSubscription.modules?.[norm(parentModule) ?? ""];
		}
		if (!moduleConfig || !moduleConfig.features) {
			return {
				allowed: true,
				locked: true,
				reason: "Please upgrade your subscription to access this feature."
			};
		}
		const featureConfig = moduleConfig.features[feature?.toLowerCase()];
		if (!featureConfig) {
			return {
				allowed: true,
				locked: true,
				reason: "Please upgrade your subscription to access this feature."
			};
		}
		const allowedAction = featureConfig?.actions.find(
			(a: string) => a.toLowerCase() === action?.toLowerCase()
		);
		if (!allowedAction) {
			return {
				allowed: true,
				locked: true,
				reason: "Please upgrade your subscription to access this feature."
			};
		}
		return { allowed: true, locked: false, reason: "" };
	};
	/** ----------------------------------------------------------------
	 *  findPermission
	 *  ----------------------------------------------------------------
	 *  Returns the FIRST permission row that:
	 *    • matches model  (mandatory)
	 *    • matches parent model if the row itself has one
	 *    • matches action
	 *    • is boolean AND enabled (value === "true")
	 *  ---------------------------------------------------------------- */
	const findPermission = (
		perms: Permission[],
		model: string,
		action: string,
		parentModel?: string         // optional in the call
	): Permission | undefined => {
		const norm = (s?: string) => s?.toLowerCase();

		const m = norm(model);
		const act = norm(action);
		const p = norm(parentModel);

		const getParent = (perm: Permission) =>
			norm((perm as any).parent_model ?? (perm as any).parentModel);

		return perms.find((perm) => {
			if (norm(perm.model) !== m) return false;                 // ① model

			const permParent = getParent(perm);
			if (permParent && permParent !== p) return false;         // ② parent

			return (
				norm(perm.name) === act &&                              // ③ action
				perm.type?.toLowerCase() === "boolean" &&               // ④ boolean
				perm.value?.toLowerCase() === "true"                    // ⑤ enabled
			);
		});
	};

	/**
	 * checkPermission function.
	 */
	const checkPermission = (
		module: string,
		action: string,
		featureKey?: string,
		skipSubscription: boolean = false,
		skipPermission: boolean = false,
		parentModel?: string
	): { allowed: boolean; locked: boolean; reason: string } => {
		let granularResult = { allowed: true, locked: false, reason: "" };
		if (featureKey && featureKey.trim() !== "") {
			granularResult = isFeatureAllowed(module, featureKey, action, parentModel);
		}

		if (!skipSubscription && granularResult.locked) {
			// Ensure reason is a string.
			return { allowed: true, locked: true, reason: granularResult.reason || "Please upgrade your subscription to access this feature." };
		}

		const isOwner = user?.role?.title.toLowerCase() === "owner";
		const effectiveSkipPermission = isOwner || skipPermission;

		const normalizedModule = module?.toLowerCase();
		const normalizedParentModel = parentModel?.toLowerCase();
		const normalizedAction = action?.toLowerCase();

		const record = findPermission(
			permissions,
			normalizedModule,
			normalizedAction,
			normalizedParentModel        // may be undefined
		);

		const traditionalAllowed = record?.value
			? record.value.toLowerCase() === "true"
			: false;


		const effectiveTraditional = effectiveSkipPermission ? true : traditionalAllowed;

		const allowed = granularResult.allowed && effectiveTraditional;
		const locked = !allowed;
		let reason = "";
		if (locked && !effectiveSkipPermission && !traditionalAllowed) {
			reason = "You do not have the required permissions. Please contact your administrator.";
		} else if (granularResult.locked) {
			reason = granularResult.reason;
		}

		// Always return a string for reason.
		return { allowed, locked, reason: reason || "" };
	};





	const checkModulePermission = (
		module: string,
		action: string,
		skipPermission?: string,
	): { allowed: boolean; locked: boolean; reason: string } => {
		let granularResult = { allowed: true, locked: false, reason: "" };
		const isOwner = user?.role?.title.toLowerCase() === "owner";
		const effectiveSkipPermission = isOwner || skipPermission;

		const normalizedModule = module?.toLowerCase();
		const normalizedAction = action?.toLowerCase();

		const record = permissions.find((permission) => {
			const permissionModule = permission?.model ? permission.model.toLowerCase() : "";
			const permissionAction = permission?.name.toLowerCase();
			const permissionType = permission?.type ? permission.type.toLowerCase() : "";
			if (permissionModule)
				return (
					permissionModule === normalizedModule &&
					permissionAction === normalizedAction
				);
		});

		const traditionalAllowed = record?.value
			? record.value.toLowerCase() !== "none" && record.value.toLowerCase() !== "false"
			: false;
		const effectiveTraditional = effectiveSkipPermission ? true : traditionalAllowed;
		const allowed = granularResult.allowed && effectiveTraditional;
		const locked = !allowed;
		let reason = "";
		if (locked && !effectiveSkipPermission && !traditionalAllowed) {
			reason = "You do not have the required permissions. Please contact your administrator.";
		} else if (granularResult.locked) {
			reason = granularResult.reason;
		}

		// Always return a string for reason.
		return { allowed, locked, reason: reason || "" };
	};

	return { checkPermission, checkModulePermission, userSubscription };
}
